import { IItemCount } from '@educraft/base';
import { PdfGen, PdfSize, PdfRect, PdfPoint, PageGen, PdfFont } from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import { GTuple, GLabelType } from '../../pdf/glabel';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/helpers';
import '../../pdf/draw/label';
import '../../pdf/draw/cut';
import {
  drawLcdTableRect,
  LCD_TABLE_WIDTH,
  LCD_TABLE_HEIGHT,
  LCD_TOP_HEIGHT,
  LCD_MARGIN,
  LCD_COLOR_PALETTES,
} from '../../pdf/board/lcdTable';

/**
 * LCD_Tabulka (Label version) - Two-sided flashcard table for LCD board.
 *
 * 2 landscape pages forming a fold-over learning tool:
 * - Page 0 (front): Question labels only in both panels.
 * - Page 1 (back):  LCD frame + question labels (top 2/3) + answer labels (bottom 1/3),
 *                   with panels physically swapped so answers appear behind questions.
 *
 * Migrated from C# EduFrame.Single.Generators.LcdTableBase
 */
function drawPanel(
  page0: PageGen,
  page1: PageGen,
  items: GTuple[],
  rect1: PdfRect,
  rect2: PdfRect,
  font: PdfFont
): void {
  const n = items.length;
  for (let i = 0; i < n; i++) {
    const cellH = rect1.height / n;
    const r1 = new PdfRect(rect1.x, rect1.y + i * cellH, rect1.width, cellH);
    const r2 = new PdfRect(rect2.x, rect2.y + i * cellH, rect2.width, cellH);

    // Top 2/3 = question; bottom 1/3 = answer (back page only)
    const r1q = new PdfRect(r1.x, r1.y, r1.width, (r1.height * 2) / 3);
    const r2q = new PdfRect(r2.x, r2.y, r2.width, (r2.height * 2) / 3);
    const r2a = new PdfRect(r2.x, r2.y + (r2.height * 2) / 3, r2.width, r2.height / 3);

    page0.drawLabel(items[i].left, { rect: r1q, font });
    page1.drawLabel(items[i].left, { rect: r2q, font });
    page1.drawLabel(items[i].right, { rect: r2a, font });

    page0.drawRectangle(GUtil.basePen, null, r1);
    page1.drawRectangle(GUtil.basePen, null, r2);
  }
}

abstract class LcdTabulkaBase implements ILayoutLabelGen, IItemCount {
  constructor(protected readonly count: number) {}

  countDefault(): number {
    return this.count * 2;
  }
  countMin(): number {
    return this.count * 2;
  }
  countMax(): number {
    return this.count * 2;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const page0 = pdf.addPage(true); // landscape
    const page1 = pdf.addPage(true); // landscape

    const originX = (PdfSize.A4H.width - 2 * LCD_TABLE_WIDTH) / 2;
    const originY = (PdfSize.A4H.height - LCD_TABLE_HEIGHT) / 2;

    const brushes = GUtil.randomBrushes(opts.data.random, LCD_COLOR_PALETTES);

    const table1 = new PdfRect(originX, originY, LCD_TABLE_WIDTH, LCD_TABLE_HEIGHT);
    const table2 = new PdfRect(
      originX + LCD_TABLE_WIDTH,
      originY,
      LCD_TABLE_WIDTH,
      LCD_TABLE_HEIGHT
    );

    const rect1 = new PdfRect(
      table1.x + LCD_MARGIN,
      table1.y + LCD_TOP_HEIGHT,
      table1.width - 2 * LCD_MARGIN,
      table1.height - LCD_TOP_HEIGHT - LCD_MARGIN
    );
    const rect2 = new PdfRect(
      table2.x + LCD_MARGIN,
      table2.y + LCD_TOP_HEIGHT,
      table2.width - 2 * LCD_MARGIN,
      table2.height - LCD_TOP_HEIGHT - LCD_MARGIN
    );

    // Draw frame on back page only
    drawLcdTableRect(page1, table1, brushes[0]);
    drawLcdTableRect(page1, table2, brushes[1]);

    page1.cutCross(new PdfPoint(originX, originY));
    page1.cutCross(new PdfPoint(originX + LCD_TABLE_WIDTH * 2, originY + LCD_TABLE_HEIGHT));
    page1.cutCross(new PdfPoint(PdfSize.A4H.width / 2, 0), false);

    // Pre-compute font so all text fits the column width
    let font = GUtil.baseFont;
    const allItems = opts.list.slice(0, this.count * 2);
    const texts = allItems
      .flatMap((item) => {
        const result: string[] = [];
        if (item.left.type === GLabelType.Text) result.push(item.left.getText());
        if (item.right.type === GLabelType.Text) result.push(item.right.getText());
        return result;
      })
      .filter(Boolean);
    if (texts.length > 0) {
      font = page0.adjustFont(rect1.width, texts, GUtil.baseFont);
    }

    const groupA = allItems.slice(0, this.count);
    const groupB = allItems.slice(this.count);

    // Group A: front left panel → back right panel (swapped for fold-over)
    drawPanel(page0, page1, groupA, rect1, rect2, font);
    // Group B: front right panel → back left panel (swapped for fold-over)
    drawPanel(page0, page1, groupB, rect2, rect1, font);
  }
}

export class LCD_Tabulka__3 extends LcdTabulkaBase {
  constructor() {
    super(3);
  }
}

export class LCD_Tabulka__5 extends LcdTabulkaBase {
  constructor() {
    super(5);
  }
}

export class LCD_Tabulka__8 extends LcdTabulkaBase {
  constructor() {
    super(8);
  }
}

export class LCD_Tabulka__10 extends LcdTabulkaBase {
  constructor() {
    super(10);
  }
}
